import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../hooks/useAuthStore';
import { Users, UserCheck, Clock, AlertTriangle, TrendingUp, ArrowRight, RefreshCw } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import clsx from 'clsx';

interface DashboardData {
  candidates: { total: number; active: number; completed: number; abandoned: number; byPhase: Record<number, number>; };
  ministry: { totalCandidates: number; activeCandidates: number; completedCandidates: number; totalMinisters: number; activeMinisters: number; };
  stagnantCandidates: Array<{ id: string; first_name: string; last_name: string; whatsapp_name: string; current_phase: number; last_interaction: string; }>;
}

const PHASE_COLORS = ['#6b7280', '#3b82f6', '#8b5cf6', '#f97316', '#22c55e'];
const PHASE_NAMES = ['Onboarding', 'Valoración', 'Rastreo', 'Ejecución', 'Seguimiento'];

export function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = async () => {
    setIsLoading(true);
    const result = await apiRequest<DashboardData>('/dashboard');
    if (result.success && result.data) {
      setData(result.data);
      setError(null);
    } else {
      setError(result.error || 'Error al cargar el dashboard');
    }
    setIsLoading(false);
  };

  useEffect(() => { fetchDashboard(); }, []);

  const phaseData = data ? Object.entries(data.candidates.byPhase).map(([phase, count]) => ({
    name: PHASE_NAMES[parseInt(phase)] || `Fase ${phase}`, value: count, phase: parseInt(phase),
  })) : [];

  const trendData = [
    { name: 'Ene', candidatos: 4 }, { name: 'Feb', candidatos: 8 }, { name: 'Mar', candidatos: 15 },
    { name: 'Abr', candidatos: 12 }, { name: 'May', candidatos: 20 }, { name: 'Jun', candidatos: 25 },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (<div key={i} className="card p-6"><div className="skeleton h-4 w-24 mb-3" /><div className="skeleton h-8 w-16" /></div>))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-warning-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Error al cargar el dashboard</h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
        <button onClick={fetchDashboard} className="btn-primary"><RefreshCw className="w-4 h-4 mr-2" />Reintentar</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Candidatos" value={data?.candidates.total || 0} icon={Users} color="primary" />
        <StatCard title="Candidatos Activos" value={data?.candidates.active || 0} icon={Clock} color="warning" />
        <StatCard title="Completados" value={data?.candidates.completed || 0} icon={UserCheck} color="success" />
        <StatCard title="Ministros Activos" value={data?.ministry.activeMinisters || 0} icon={TrendingUp} color="purple" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tendencia de Candidatos</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData}>
                <defs><linearGradient id="colorCandidatos" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#5865F2" stopOpacity={0.3} /><stop offset="95%" stopColor="#5865F2" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis dataKey="name" stroke="#6b7280" /><YAxis stroke="#6b7280" />
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#fff' }} />
                <Area type="monotone" dataKey="candidatos" stroke="#5865F2" fillOpacity={1} fill="url(#colorCandidatos)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Distribución por Fase</h3>
          <div className="h-64 flex items-center">
            {phaseData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={phaseData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                    {phaseData.map((entry, index) => (<Cell key={`cell-${index}`} fill={PHASE_COLORS[entry.phase] || '#6b7280'} />))}
                  </Pie>
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0];
                    const phase = d.payload?.phase ?? 0;
                    return (
                      <div style={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', padding: '8px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: PHASE_COLORS[phase] || '#6b7280' }} />
                          <span style={{ color: '#fff', fontSize: '13px', fontWeight: 500 }}>{d.name}</span>
                        </div>
                        <div style={{ color: '#d1d5db', fontSize: '12px', marginTop: '2px', paddingLeft: '16px' }}>
                          {d.value} candidato{d.value !== 1 ? 's' : ''}
                        </div>
                      </div>
                    );
                  }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (<div className="empty-state w-full"><p className="text-gray-500 dark:text-gray-400">No hay datos</p></div>)}
            <div className="flex flex-col gap-2 ml-4">
              {PHASE_NAMES.map((name, index) => (<div key={index} className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: PHASE_COLORS[index] }} /><span className="text-sm text-gray-600 dark:text-gray-400">{name}</span></div>))}
            </div>
          </div>
        </div>
      </div>

      {/* Stagnant Candidates */}
      {data?.stagnantCandidates && data.stagnantCandidates.length > 0 && (
        <div className="card border-warning-500/50">
          <div className="p-4 border-b border-gray-200 dark:border-dark-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-warning-100 dark:bg-warning-500/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-warning-600 dark:text-warning-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Candidatos Sin Actividad</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{data.stagnantCandidates.length} sin interacción en 7 días</p>
              </div>
            </div>
            <Link to="/candidates?filter=stagnant" className="btn-secondary btn-sm">Ver todos<ArrowRight className="w-4 h-4 ml-1" /></Link>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-dark-700">
            {data.stagnantCandidates.slice(0, 5).map((c) => (
              <Link key={c.id} to={`/candidates/${c.id}`} className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-dark-700/50">
                <div className="flex items-center gap-3">
                  <div className="avatar avatar-sm">{(c.first_name || c.whatsapp_name || '?').charAt(0).toUpperCase()}</div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{c.first_name ? `${c.first_name} ${c.last_name || ''}` : c.whatsapp_name || 'Sin nombre'}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Fase {c.current_phase}: {PHASE_NAMES[c.current_phase]}</p>
                  </div>
                </div>
                <span className="badge badge-warning">{getDaysAgo(c.last_interaction)} días</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickActionCard title="Ver Candidatos" description="Gestiona candidatos en proceso" href="/candidates" icon={Users} />
        <QuickActionCard title="Gestionar Ministros" description="Administra el equipo" href="/ministers" icon={UserCheck} />
        <QuickActionCard title="Configuración" description="Configura tu ministerio" href="/settings" icon={TrendingUp} />
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color }: { title: string; value: number; icon: React.ElementType; color: 'primary' | 'success' | 'warning' | 'purple' }) {
  const colorClasses = {
    primary: 'bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400',
    success: 'bg-success-100 dark:bg-success-500/20 text-success-600 dark:text-success-400',
    warning: 'bg-warning-100 dark:bg-warning-500/20 text-warning-600 dark:text-warning-400',
    purple: 'bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400',
  };
  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <div><p className="text-sm text-gray-500 dark:text-gray-400">{title}</p><p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{value}</p></div>
        <div className={clsx('w-12 h-12 rounded-xl flex items-center justify-center', colorClasses[color])}><Icon className="w-6 h-6" /></div>
      </div>
    </div>
  );
}

function QuickActionCard({ title, description, href, icon: Icon }: { title: string; description: string; href: string; icon: React.ElementType }) {
  return (
    <Link to={href} className="card-hover p-6 group">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-primary-100 dark:bg-primary-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
          <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        </div>
        <div className="flex-1"><h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3><p className="text-sm text-gray-500 dark:text-gray-400">{description}</p></div>
        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-all" />
      </div>
    </Link>
  );
}

function getDaysAgo(dateString: string): number {
  const diffTime = Math.abs(new Date().getTime() - new Date(dateString).getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
